import React from "react"
import styled from "styled-components"
import { ContentfulContentBaseProps } from "../types"
import FeaturedProductList from "@components/FeaturedProductList"
import useEnhancedEcommerceEvents from "@hooks/useEnhancedEcommerceEvents"
import useElevarEvents from "@hooks/useElevarEvents"
import useScrollHandler from "@hooks/useScrollHandler"
import { getIn } from "@utils/index"
import { getLocalStorage, writeLocalStorage } from "@utils/storage"
import { SELECTED_PRODUCTS_KEY } from "@constants/storageKeys"

const Container = styled.div``

interface ContentfulFeaturedProductListProps
  extends ContentfulContentBaseProps {}

const ContentfulFeaturedProductList: React.FC<
  ContentfulFeaturedProductListProps
> = ({
  blockId,
  title,
  titleShort,
  mobileHero,
  hero,
  link,
  linkText,
  linkTextShort,
  productList,
}) => {
  const { onViewProductList, onClickProductCard } = useEnhancedEcommerceEvents()
  const { elevarOnViewItemList, elevarOnSelectItem } = useElevarEvents()

  const productListWithPrices = React.useMemo(
    () =>
      productList
        .filter((product: any) => product.variants?.length > 0)
        .map((product: any, i) => {
          const shopifyProductId = getIn(
            product,
            "shopifyProduct.shopifyProductId"
          )

          let multipleDevices
          if (product.devices) {
            multipleDevices = product.devices.reduce((acc, curr) => {
              if (acc) {
                acc = acc + `, ${curr.name}`
              } else {
                acc = curr.name
              }

              return acc
            }, undefined)
          }

          const gtmProduct = {
            productId: shopifyProductId,
            productName: product.productName,
            variantName: product.variants[0].name,
            sku: product.variants[0].sku,
            price: product.variants[0].price,
            brand: product.brand.name,
            device: multipleDevices ? multipleDevices : product.device.name,
            deviceType: product.device.type.name,
            list: `Featured Product List: ${title}`,
          }

          let list = ""

          if (typeof window !== "undefined") {
            list = getIn(window, "location.pathname")
          }

          const elevarProduct = {
            sku: product.variants[0].sku,
            productId: shopifyProductId,
            variantId: product.variants[0].shopifyVariantId,
            productName: product.productName,
            variantName: product.variants[0].name,
            price: product.variants[0].price,
            brand: "Urban Armor Gear",
            device: multipleDevices ? multipleDevices : product.device.name,
            deviceType: product.device.type.name,
            position: i + 1,
            list,
          }

          return {
            ...product,
            gtmProduct,
            elevarProduct,
            onClick: () => {
              const selectedProducts =
                getLocalStorage(SELECTED_PRODUCTS_KEY) || {}
              writeLocalStorage(SELECTED_PRODUCTS_KEY, {
                ...selectedProducts,
                [elevarProduct.productId]: elevarProduct.list,
              })

              onClickProductCard(gtmProduct)
              elevarOnSelectItem(elevarProduct, elevarProduct.list)
            },
          }
        }),
    [productList]
  )

  const [onViewProductListCalled, setOnViewProductListCalled] =
    React.useState<boolean>(false)

  const containerRef = React.useRef<HTMLDivElement>(null)

  const onScroll = () => {
    if (
      !onViewProductListCalled &&
      typeof window !== "undefined" &&
      containerRef &&
      containerRef.current &&
      productListWithPrices[0].variants[0].price
    ) {
      if (
        containerRef.current.getBoundingClientRect().top < window.innerHeight
      ) {
        const gtmProducts = productListWithPrices.map(
          ({ gtmProduct }) => gtmProduct
        )
        const elevarProducts = productListWithPrices.map(
          ({ elevarProduct }) => elevarProduct
        )
        onViewProductList(gtmProducts)
        elevarOnViewItemList(elevarProducts)
        setOnViewProductListCalled(true)
      }
    }
  }

  useScrollHandler({
    onScroll,
    runForProps: [containerRef, productListWithPrices, onViewProductListCalled],
  })

  return (
    <Container ref={containerRef} id={blockId || undefined}>
      <FeaturedProductList
        title={title}
        titleShort={titleShort}
        mobileHero={getIn(mobileHero, "file.url")}
        hero={getIn(hero, "file.url")}
        productList={productListWithPrices}
        link={link}
        linkText={linkText}
        linkTextShort={linkTextShort}
      />
    </Container>
  )
}

export default ContentfulFeaturedProductList
